import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Users, ChefHat, Heart, AlertCircle, Edit2, Trash2, Building2, MapPin, PlusCircle, Beer } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../contexts/LanguageContext';
import LoadingIndicator from '../components/LoadingIndicator';
import BackButton from '../components/BackButton';

const MyEvents = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [hasVenue, setHasVenue] = useState(false);
  const [venue, setVenue] = useState<any>(null);
  const [venueType, setVenueType] = useState<'restaurant' | 'bar' | null>(null);

  useEffect(() => {
    checkVenueAndLoadEvents();
  }, []);

  const checkVenueAndLoadEvents = async () => {
    try {
      setLoading(true);
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) {
        navigate('/login');
        return;
      }

      // Check if user owns a restaurant or bar
      const { data: restaurant, error: restaurantError } = await supabase
        .from('restaurants')
        .select('*')
        .eq('owner_id', user.id)
        .maybeSingle();

      if (restaurantError && restaurantError.code !== 'PGRST116') {
        throw restaurantError;
      }

      const { data: bar, error: barError } = await supabase
        .from('bars')
        .select('*')
        .eq('owner_id', user.id)
        .maybeSingle();

      if (barError && barError.code !== 'PGRST116') {
        throw barError;
      }

      if (restaurant || bar) {
        setHasVenue(true);
        setVenue(restaurant || bar);
        setVenueType(restaurant ? 'restaurant' : 'bar');
      }

      // Load all events
      await loadEvents(user.id, restaurant?.id, bar?.id);
    } catch (err) {
      console.error('Error checking venue ownership:', err);
      setError(err instanceof Error ? err.message : 'Failed to check venue ownership');
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async (userId: string, restaurantId?: string, barId?: string) => {
    try {
      // Fetch dinners where user is host or restaurant owner
      const { data: dinners, error: dinnersError } = await supabase
        .from('dinners')
        .select(`
          *,
          restaurant:restaurants(*)
        `)
        .or(`host_id.eq.${userId}${restaurantId ? `,restaurant_id.eq.${restaurantId}` : ''}`)
        .order('date', { ascending: true })
        .order('time', { ascending: true });

      if (dinnersError) throw dinnersError;

      // Fetch blind dates
      const { data: blindDates, error: blindDatesError } = await supabase
        .from('blind_dates')
        .select(`
          *,
          restaurant:restaurants(*),
          bar:bars(*)
        `)
        .or(`host_id.eq.${userId}${restaurantId ? `,restaurant_id.eq.${restaurantId}` : ''}${barId ? `,bar_id.eq.${barId}` : ''}`)
        .order('date', { ascending: true })
        .order('time', { ascending: true });

      if (blindDatesError) throw blindDatesError;

      // Fetch drinking nights
      const { data: drinkingNights, error: drinkingNightsError } = await supabase
        .from('drinking_nights')
        .select(`
          *,
          bar:bars(*)
        `)
        .or(`host_id.eq.${userId}${barId ? `,bar_id.eq.${barId}` : ''}`)
        .order('date', { ascending: true })
        .order('time', { ascending: true });

      if (drinkingNightsError) throw drinkingNightsError;

      // Combine and sort all events
      const allEvents = [
        ...(dinners || []).map(event => ({ ...event, type: 'dinner' as const })),
        ...(blindDates || []).map(event => ({ ...event, type: 'blind_date' as const })),
        ...(drinkingNights || []).map(event => ({ ...event, type: 'drinking_night' as const }))
      ].sort((a, b) => {
        const dateA = new Date(`${a.date}T${a.time}`);
        const dateB = new Date(`${b.date}T${b.time}`);
        return dateA.getTime() - dateB.getTime();
      });

      setEvents(allEvents);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load events');
    }
  };

  const handleCancelEvent = async (event: any) => {
    if (!confirm(t('common.confirmCancel'))) return;

    try {
      const table = event.type === 'blind_date' ? 'blind_dates' : 
                    event.type === 'dinner' ? 'dinners' : 'drinking_nights';
      
      const { error } = await supabase
        .from(table)
        .update({ status: 'cancelled' })
        .eq('id', event.id);

      if (error) throw error;

      // Refresh events
      const { data: { user } } = await supabase.auth.getUser();
      if (user && venue) {
        await loadEvents(user.id, venue.id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel event');
    }
  };

  if (loading) {
    return <LoadingIndicator message={t('common.loading')} />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <BackButton />
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('myEvents.title')}</h1>
            <p className="text-gray-600 mt-1">
              {t('myEvents.subtitle')} {venue?.name}
            </p>
          </div>
          <div className="flex space-x-4">
            <button
              onClick={() => navigate('/create-dinner')}
              className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition flex items-center space-x-2"
            >
              <PlusCircle className="h-5 w-5" />
              <span>{t('myEvents.createDinner')}</span>
            </button>
            <button
              onClick={() => navigate('/create-drinking-night')}
              className="bg-emerald-500 text-white px-4 py-2 rounded-md hover:bg-emerald-600 transition flex items-center space-x-2"
            >
              <Beer className="h-5 w-5" />
              <span>{t('myEvents.createBarNight')}</span>
            </button>
            <button
              onClick={() => navigate('/create-blind-date')}
              className="bg-orange-500 text-white px-4 py-2 rounded-md hover:bg-orange-600 transition flex items-center space-x-2"
            >
              <Heart className="h-5 w-5" />
              <span>{t('myEvents.hostBlindDate')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Events Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {events.map((event) => {
          const venue = event.restaurant || event.bar;
          const isBlindDate = event.type === 'blind_date';
          const isDrinkingNight = event.type === 'drinking_night';

          return (
            <div
              key={`${event.type}-${event.id}`}
              className={`bg-white rounded-lg shadow-md overflow-hidden ${
                event.status === 'cancelled' ? 'opacity-75' : ''
              } ${isBlindDate ? 'border-l-4 border-orange-500' : 
                  isDrinkingNight ? 'border-l-4 border-emerald-500' : 
                  'border-l-4 border-blue-500'}`}
            >
              <div className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center space-x-2 text-gray-800 font-medium">
                      {isDrinkingNight ? (
                        <>
                          <Beer className="h-5 w-5 text-emerald-500" />
                          <span>{venue.name}</span>
                        </>
                      ) : isBlindDate ? (
                        <>
                          <Heart className="h-5 w-5 text-orange-500" />
                          <span>{venue.name}</span>
                        </>
                      ) : (
                        <>
                          <ChefHat className="h-5 w-5 text-blue-500" />
                          <span>{venue.name}</span>
                        </>
                      )}
                      <span className={`text-sm px-2 py-1 rounded ${
                        isDrinkingNight ? 'bg-emerald-100 text-emerald-600' :
                        isBlindDate ? 'bg-orange-100 text-orange-600' : 
                        'bg-blue-100 text-blue-600'
                      }`}>
                        {isDrinkingNight ? t('myEvents.eventType.barNight') :
                         isBlindDate ? t('myEvents.eventType.blindDate') : 
                         t('myEvents.eventType.dinner')}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-600 mt-1">
                      <MapPin className="h-4 w-4" />
                      <span>{venue.address}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(event.date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Clock className="h-4 w-4" />
                      <span>{event.time.slice(0, 5)}</span>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    event.status === 'open' ? 'bg-green-100 text-green-700' :
                    event.status === 'full' ? 'bg-orange-100 text-orange-700' :
                    event.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {t(`myEvents.status.${event.status}`)}
                  </span>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Users className="h-4 w-4" />
                    <span>
                      {t('myEvents.spots')} {event.spots_available} / {event.spots_total}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {event.age_range && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-600 rounded-full text-sm">
                        {t('myEvents.ageRange')} {event.age_range}
                      </span>
                    )}
                    {event.preferred_gender && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-600 rounded-full text-sm">
                        {t('myEvents.gender')} {event.preferred_gender}
                      </span>
                    )}
                    {(event.dining_style || event.style) && (
                      <span className="px-2 py-1 bg-green-100 text-green-600 rounded-full text-sm">
                        {t('myEvents.style')} {event.dining_style || event.style}
                      </span>
                    )}
                  </div>

                  <p className="text-gray-600 text-sm line-clamp-2">
                    {event.description}
                  </p>
                </div>

                {event.status !== 'cancelled' && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => navigate(`/events/${event.id}/edit`)}
                      className="flex-1 bg-gray-100 text-gray-600 px-4 py-2 rounded-md hover:bg-gray-200 transition flex items-center justify-center space-x-2"
                    >
                      <Edit2 className="h-4 w-4" />
                      <span>{t('myEvents.edit')}</span>
                    </button>
                    <button
                      onClick={() => handleCancelEvent(event)}
                      className="flex-1 bg-red-100 text-red-600 px-4 py-2 rounded-md hover:bg-red-200 transition flex items-center justify-center space-x-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>{t('myEvents.cancel')}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {events.length === 0 && (
          <div className="md:col-span-2 lg:col-span-3 text-center py-12">
            <Heart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('myEvents.noEvents')}</h3>
            <p className="text-gray-600 mb-4">
              {t('myEvents.noEventsMessage')}
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => navigate('/create-dinner')}
                className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 transition inline-flex items-center space-x-2"
              >
                <PlusCircle className="h-5 w-5" />
                <span>{t('myEvents.createDinner')}</span>
              </button>
              <button
                onClick={() => navigate('/create-drinking-night')}
                className="bg-emerald-500 text-white px-6 py-2 rounded-md hover:bg-emerald-600 transition inline-flex items-center space-x-2"
              >
                <Beer className="h-5 w-5" />
                <span>{t('myEvents.createBarNight')}</span>
              </button>
              <button
                onClick={() => navigate('/create-blind-date')}
                className="bg-orange-500 text-white px-6 py-2 rounded-md hover:bg-orange-600 transition inline-flex items-center space-x-2"
              >
                <Heart className="h-5 w-5" />
                <span>{t('myEvents.hostBlindDate')}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyEvents;